/* Africancreates: native muted autoplay starts on page open, without load/idle delays. */
(() => {
  if (!window.customElements || customElements.get('ac-artisan-film')) return;
  class ArtisanFilm extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const video = this.querySelector('[data-ac-artisan-video]');
      const button = this.querySelector('[data-ac-film-toggle]');
      if (!video || !button) return;
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const listeners = [];
      let intent = 'auto', visible = true, failed = false;
      let pending = false, blocked = false, disposed = false, retryId;
      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      video.preload = 'auto';
      const listen = (target, name, fn) => {
        if (!target || !target.addEventListener) return;
        target.addEventListener(name, fn);
        listeners.push(() => target.removeEventListener(name, fn));
      };
      const shouldPlay = () => !disposed && this.isConnected && !document.hidden && visible && !failed && !blocked && intent !== 'pause' && (intent === 'play' || !motion.matches);
      const syncLabel = () => {
        button.textContent = video.paused ? button.dataset.play : button.dataset.pause;
      };
      const update = () => {
        const play = shouldPlay();
        video.autoplay = play;
        if (!play) { video.pause(); syncLabel(); return; }
        if (!video.paused || pending) { syncLabel(); return; }
        video.muted = true;
        pending = true;
        const result = video.play();
        if (!result || !result.then) { pending = false; syncLabel(); return; }
        result.then(() => {
          pending = false;
          if (!shouldPlay()) video.pause();
          syncLabel();
        }).catch(error => {
          pending = false;
          if (error.name !== 'AbortError') {
            blocked = true;
            video.autoplay = false;
          }
          syncLabel();
          if (error.name === 'AbortError' && shouldPlay()) retryId = setTimeout(update, 0);
        });
      };
      listen(button, 'click', () => {
        if (!video.paused || pending) {
          intent = 'pause';
          video.autoplay = false;
          video.pause();
        } else {
          intent = 'play';
          blocked = false;
          update();
        }
        syncLabel();
      });
      listen(video, 'playing', () => {
        if (!shouldPlay()) { video.pause(); return; }
        video.classList.add('is-ready');
        syncLabel();
      });
      listen(video, 'pause', syncLabel);
      listen(video, 'error', () => {
        failed = true;
        video.classList.add('is-failed');
        video.autoplay = false;
        video.pause();
        button.hidden = true;
      });
      listen(document, 'visibilitychange', update);
      listen(window, 'pagehide', () => { video.autoplay = false; video.pause(); });
      listen(window, 'pageshow', update);
      const preferenceChanged = () => {
        if (intent !== 'pause') intent = 'auto';
        blocked = false;
        update();
      };
      if (motion.addEventListener) listen(motion, 'change', preferenceChanged);
      else if (motion.addListener) {
        motion.addListener(preferenceChanged);
        listeners.push(() => motion.removeListener(preferenceChanged));
      }
      const checkVisible = () => {
        const rect = this.getBoundingClientRect();
        visible = rect.bottom > 0 && rect.top < window.innerHeight && rect.width > 0;
      };
      checkVisible();
      let observer;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => {
          visible = entries[0].isIntersecting;
          update();
        }, { threshold: 0 });
        observer.observe(this);
      } else {
        const onViewportChange = () => { checkVisible(); update(); };
        listen(window, 'scroll', onViewportChange);
        listen(window, 'resize', onViewportChange);
      }
      this.cleanup = () => {
        disposed = true;
        video.autoplay = false;
        video.pause();
        if (observer) observer.disconnect();
        clearTimeout(retryId);
        listeners.forEach(remove => remove());
        button.hidden = true;
        video.controls = true;
      };
      // Native controls are the no-JavaScript fallback; replace only after binding the button.
      video.controls = false;
      button.hidden = false;
      syncLabel();
      update();
    }
    disconnectedCallback() {
      if (this.cleanup) this.cleanup();
      this.cleanup = null;
    }
  }
  customElements.define('ac-artisan-film', ArtisanFilm);
})();
